// Word documents, read in the browser. A .docx is a zip of XML files with the writing in
// word/document.xml. It's written out as HTML, which markdown-html.ts reads as it reads writing
// pasted from Word: headings, bold, italics, underline, strikethrough, raised and lowered text,
// colors, highlights, links, lists, quotes, and tables.

interface ZipEntry {
  /** 0 for stored as it is, 8 for deflated. */
  readonly method: number;
  readonly size: number;
  /** Where the entry's local header starts. */
  readonly header: number;
}

// The colors Word highlights in, by the names it gives them.
const HIGHLIGHTS: Readonly<Record<string, string>> = {
  yellow: '#ffff00',
  green: '#00ff00',
  cyan: '#00ffff',
  magenta: '#ff00ff',
  blue: '#0000ff',
  red: '#ff0000',
  darkBlue: '#000080',
  darkCyan: '#008080',
  darkGreen: '#008000',
  darkMagenta: '#800080',
  darkRed: '#800000',
  darkYellow: '#808000',
};

/** The document's writing as HTML; null when it isn't a Word document this can read. */
export async function docxHtml(data: ArrayBuffer): Promise<string | null> {
  const bytes = new Uint8Array(data);
  const entries = zipEntries(bytes);
  const main = entries?.get('word/document.xml');
  if (!entries || !main) return null;
  const read = async (name: string) => {
    const entry = entries.get(name);
    return entry ? parseXml(await unzip(bytes, entry)) : null;
  };
  const [document, styles, numbering, relations] = await Promise.all([
    unzip(bytes, main).then(parseXml),
    read('word/styles.xml'),
    read('word/numbering.xml'),
    read('word/_rels/document.xml.rels'),
  ]);
  const body = document && child(document.documentElement, 'body');
  if (!body) return null;
  return new DocxWriter(styles, numbering, relations).write(body);
}

/** The files in a zip, by name; null when it isn't a zip. */
function zipEntries(bytes: Uint8Array): Map<string, ZipEntry> | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  try {
    // The end of the central directory: the last record, before a comment of up to 64 KB.
    let end = -1;
    for (let at = bytes.length - 22; at >= Math.max(0, bytes.length - 22 - 0xffff); at -= 1) {
      if (view.getUint32(at, true) === 0x06054b50) {
        end = at;
        break;
      }
    }
    if (end < 0) return null;
    const count = view.getUint16(end + 10, true);
    let at = view.getUint32(end + 16, true);
    const names = new TextDecoder();
    const entries = new Map<string, ZipEntry>();
    for (let index = 0; index < count; index += 1) {
      if (view.getUint32(at, true) !== 0x02014b50) return null;
      const nameLength = view.getUint16(at + 28, true);
      const name = names.decode(bytes.subarray(at + 46, at + 46 + nameLength));
      entries.set(name, {
        method: view.getUint16(at + 10, true),
        size: view.getUint32(at + 20, true),
        header: view.getUint32(at + 42, true),
      });
      at += 46 + nameLength + view.getUint16(at + 30, true) + view.getUint16(at + 32, true);
    }
    return entries;
  } catch {
    // Read past the end: a zip cut short, or not a zip.
    return null;
  }
}

/** A file in the zip, as text. */
async function unzip(bytes: Uint8Array, entry: ZipEntry): Promise<string> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.getUint32(entry.header, true) !== 0x04034b50) throw new Error('Not a zip entry');
  const start =
    entry.header +
    30 +
    view.getUint16(entry.header + 26, true) +
    view.getUint16(entry.header + 28, true);
  const data = bytes.slice(start, start + entry.size);
  if (entry.method === 0) return new TextDecoder().decode(data);
  if (entry.method !== 8) throw new Error('Unknown compression');
  const stream = new DecompressionStream('deflate-raw');
  const writer = stream.writable.getWriter();
  // A failure shows when the text is read.
  writer.write(data).catch(() => undefined);
  writer.close().catch(() => undefined);
  return new Response(stream.readable).text();
}

function parseXml(text: string): XMLDocument | null {
  const document = new DOMParser().parseFromString(text, 'application/xml');
  return document.getElementsByTagName('parsererror').length > 0 ? null : document;
}

/** The first child of `element` called `name`, whatever its namespace's prefix. */
function child(element: Element | null | undefined, name: string): Element | undefined {
  if (!element) return undefined;
  for (const node of element.children) if (node.localName === name) return node;
  return undefined;
}

/** An attribute of `element` called `name`, as w:val or r:id, whatever its prefix. */
function attribute(element: Element | null | undefined, name: string): string | null {
  if (!element) return null;
  for (const node of element.attributes) if (node.localName === name) return node.value;
  return null;
}

/** Whether a property such as bold is on: there, and not turned off. */
function on(property: Element | undefined): boolean {
  return (
    property !== undefined && !/^(?:0|false|off|none)$/iu.test(attribute(property, 'val') ?? '')
  );
}

function escape(text: string): string {
  return text.replace(/[&<>"]/gu, (char) => `&#${String(char.charCodeAt(0))};`);
}

interface Style {
  /** Its name, as "heading 1" or "Quote", lowercase, the same in every language. */
  readonly name: string;
  /** Its run properties, for a character style such as Strong. */
  readonly run: Element | undefined;
  /** Its list, for a paragraph style such as List Bullet. */
  readonly list: Element | undefined;
}

interface Block {
  readonly html: string;
  /** In a list: how deep, from 0, and whether it's numbered. */
  readonly list?: { readonly level: number; readonly ordered: boolean };
}

class DocxWriter {
  private readonly styles = new Map<string, Style>();
  private readonly links = new Map<string, string>();
  private readonly lists = new Map<string, Element>();

  constructor(
    styles: XMLDocument | null,
    numbering: XMLDocument | null,
    relations: XMLDocument | null,
  ) {
    for (const style of styles?.documentElement.children ?? []) {
      if (style.localName !== 'style') continue;
      const id = attribute(style, 'styleId');
      if (!id) continue;
      this.styles.set(id, {
        name: (attribute(child(style, 'name'), 'val') ?? id).toLowerCase(),
        run: child(style, 'rPr'),
        list: child(child(style, 'pPr'), 'numPr'),
      });
    }
    for (const relation of relations?.documentElement.children ?? []) {
      const id = attribute(relation, 'Id');
      const target = attribute(relation, 'Target');
      if (id && target && attribute(relation, 'Type')?.endsWith('/hyperlink'))
        this.links.set(id, target);
    }
    // Each list is a numbering, whose levels are said in an abstract one: bullets or numbers.
    const abstract = new Map<string, Element>();
    const numbers: [string, string][] = [];
    for (const node of numbering?.documentElement.children ?? []) {
      if (node.localName === 'abstractNum') {
        const id = attribute(node, 'abstractNumId');
        if (id) abstract.set(id, node);
      } else if (node.localName === 'num') {
        const id = attribute(node, 'numId');
        const of = attribute(child(node, 'abstractNumId'), 'val');
        if (id && of) numbers.push([id, of]);
      }
    }
    for (const [id, of] of numbers) {
      const levels = abstract.get(of);
      if (levels) this.lists.set(id, levels);
    }
  }

  write(body: Element): string {
    let html = '';
    // The lists open, innermost last: whether each is numbered.
    const open: boolean[] = [];
    const closeTo = (depth: number) => {
      while (open.length > depth) html += `</li></${open.pop() ? 'ol' : 'ul'}>`;
    };
    for (const block of this.blocks(body)) {
      if (!block.list) {
        closeTo(0);
        html += block.html;
        continue;
      }
      const { level, ordered } = block.list;
      closeTo(level + 1);
      if (open.length === level + 1) {
        if (open[level] === ordered) html += '</li>';
        else closeTo(level);
      }
      while (open.length < level + 1) {
        html += ordered ? '<ol>' : '<ul>';
        open.push(ordered);
      }
      html += `<li>${block.html}`;
    }
    closeTo(0);
    return html;
  }

  /** The paragraphs and tables in `parent`, as in the body, a table cell, or a content control. */
  private blocks(parent: Element): Block[] {
    const blocks: Block[] = [];
    for (const node of parent.children) {
      switch (node.localName) {
        case 'p':
          blocks.push(this.paragraph(node));
          break;
        case 'tbl':
          blocks.push({ html: this.table(node) });
          break;
        case 'sdt':
          blocks.push(...this.blocks(child(node, 'sdtContent') ?? node));
          break;
        case 'customXml':
          blocks.push(...this.blocks(node));
          break;
        default:
          break;
      }
    }
    return blocks;
  }

  private paragraph(paragraph: Element): Block {
    const properties = child(paragraph, 'pPr');
    const style = this.styles.get(attribute(child(properties, 'pStyle'), 'val') ?? '');
    const words = this.inline(paragraph);
    const list = this.listOf(child(properties, 'numPr') ?? style?.list);
    if (list) return { html: words, list };
    const name = style?.name ?? '';
    const outline = attribute(child(properties, 'outlineLvl'), 'val');
    const level =
      name === 'title'
        ? 1
        : /^heading (\d)$/u.test(name)
          ? Number(name.slice(-1))
          : outline !== null && Number(outline) < 9
            ? Number(outline) + 1
            : 0;
    if (level > 0 && words) {
      const tag = `h${String(Math.min(level, 6))}`;
      return { html: `<${tag}>${words}</${tag}>` };
    }
    if (name.includes('quote')) return { html: `<blockquote><p>${words}</p></blockquote>` };
    return { html: `<p>${words}</p>` };
  }

  /** The list a paragraph is an item of: how deep, and whether it's numbered. */
  private listOf(numbering: Element | undefined): Block['list'] {
    const id = attribute(child(numbering, 'numId'), 'val');
    // Numbering 0 takes a list away.
    if (!numbering || !id || id === '0') return undefined;
    const level = Number(attribute(child(numbering, 'ilvl'), 'val') ?? 0) || 0;
    let format = 'bullet';
    for (const node of this.lists.get(id)?.children ?? []) {
      if (node.localName === 'lvl' && Number(attribute(node, 'ilvl')) === level) {
        format = attribute(child(node, 'numFmt'), 'val') ?? format;
        break;
      }
    }
    return { level: Math.min(level, 8), ordered: format !== 'bullet' && format !== 'none' };
  }

  private table(table: Element): string {
    let html = '<table>';
    for (const row of table.children) {
      if (row.localName !== 'tr') continue;
      html += '<tr>';
      for (const cell of row.children) {
        if (cell.localName !== 'tc') continue;
        html += `<td>${this.blocks(cell)
          .map((block) => block.html)
          .join('')}</td>`;
      }
      html += '</tr>';
    }
    return `${html}</table>`;
  }

  /** A paragraph's words, as they're formatted. */
  private inline(parent: Element): string {
    let html = '';
    for (const node of parent.children) {
      switch (node.localName) {
        case 'r':
          html += this.run(node);
          break;
        case 'hyperlink': {
          const href = this.links.get(attribute(node, 'id') ?? '');
          const words = this.inline(node);
          html += href ? `<a href="${escape(href)}">${words}</a>` : words;
          break;
        }
        case 'sdt':
          html += this.inline(child(node, 'sdtContent') ?? node);
          break;
        // Changes tracked, the writing as it would be with them made.
        case 'ins':
        case 'moveTo':
        case 'smartTag':
        case 'customXml':
        case 'fldSimple':
          html += this.inline(node);
          break;
        default:
          break;
      }
    }
    return html;
  }

  private run(run: Element): string {
    const own = child(run, 'rPr');
    if (on(child(own, 'vanish'))) return '';
    let text = '';
    for (const node of run.children) {
      switch (node.localName) {
        case 't':
          text += escape(node.textContent);
          break;
        case 'tab':
        case 'ptab':
          text += ' ';
          break;
        case 'br':
          // A page or column break isn't a line of the writing's own.
          text += /^(?:page|column)$/u.test(attribute(node, 'type') ?? '') ? ' ' : '<br>';
          break;
        case 'cr':
          text += '<br>';
          break;
        case 'noBreakHyphen':
          text += '-';
          break;
        default:
          break;
      }
    }
    if (!text) return '';
    // A character style, such as Strong, under the run's own formatting.
    const styled = this.styles.get(attribute(child(own, 'rStyle'), 'val') ?? '')?.run;
    const property = (name: string) => child(own, name) ?? child(styled, name);
    const tags: string[] = [];
    if (on(property('b'))) tags.push('b');
    if (on(property('i'))) tags.push('i');
    if (on(property('u'))) tags.push('u');
    if (on(property('strike')) || on(property('dstrike'))) tags.push('s');
    const raise = attribute(property('vertAlign'), 'val');
    if (raise === 'superscript') tags.push('sup');
    else if (raise === 'subscript') tags.push('sub');
    const styles: string[] = [];
    const color = attribute(property('color'), 'val');
    if (color && /^[0-9a-f]{6}$/iu.test(color)) styles.push(`color: #${color}`);
    const highlight = HIGHLIGHTS[attribute(property('highlight'), 'val') ?? ''];
    const shade = attribute(property('shd'), 'fill');
    if (highlight) styles.push(`background-color: ${highlight}`);
    else if (shade && /^[0-9a-f]{6}$/iu.test(shade)) styles.push(`background-color: #${shade}`);
    let html = text;
    for (const tag of tags.reverse()) html = `<${tag}>${html}</${tag}>`;
    return styles.length > 0 ? `<span style="${styles.join('; ')}">${html}</span>` : html;
  }
}
